import { useState } from "react";
import { isValidDashboardSlug } from "../../luzmo/custom-dashboard-config";

import type { EditableCustomDashboardConfig } from "../../luzmo/custom-dashboard-config";

interface CustomDashboardSettingsState {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  slug: string;
  setSlug: (slug: string) => void;
  tabName: string;
  setTabName: (tabName: string) => void;
  attemptedSave: boolean;
  saving: boolean;
  slugError: boolean;
  tabNameError: boolean;
  save: () => Promise<void>;
}

export const useCustomDashboardSettings = (
  initialValue: EditableCustomDashboardConfig,
  onSave: (value: EditableCustomDashboardConfig) => Promise<void | undefined>,
): CustomDashboardSettingsState => {
  const [enabled, setEnabled] = useState(initialValue.enabled);
  const [slug, setSlug] = useState(initialValue.slug);
  const [tabName, setTabName] = useState(initialValue.tabName);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const normalizedSlug = slug.trim();
  const normalizedTabName = tabName.trim();
  const slugError = enabled && !isValidDashboardSlug(normalizedSlug);
  const tabNameError = enabled && !normalizedTabName;

  const save = async (): Promise<void> => {
    setAttemptedSave(true);
    if (slugError || tabNameError) return;

    setSaving(true);
    try {
      await onSave({
        id: initialValue.id,
        enabled,
        slug: normalizedSlug,
        tabName: normalizedTabName,
      });
    } finally {
      setSaving(false);
    }
  };

  return {
    enabled,
    setEnabled,
    slug,
    setSlug,
    tabName,
    setTabName,
    attemptedSave,
    saving,
    slugError,
    tabNameError,
    save,
  };
};
